import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const ProstheticsOrthoticsCoursePage: React.FC = () => {
  const prostheticsData = {
    title: "Bachelor of Science in Prosthetics & Orthotics",
    shortName: "B.Sc P&O",
    duration: "4 Years",
    description: "A specialized program focusing on the design, fabrication, and fitting of artificial limbs (prosthetics) and orthotic devices for rehabilitation.",
    overview: "B.Sc Prosthetics & Orthotics prepares students to become certified prosthetists and orthotists who design, create, and fit artificial limbs and supportive devices. The program covers anatomy, biomechanics, materials science, device design, and patient rehabilitation. Students learn to assess patients, design custom prosthetic and orthotic devices, and provide ongoing care and adjustments for optimal function and comfort.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology/Mathematics",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years",
      "Good manual skills and creativity",
      "Empathy for disabled patients",
      "Interest in engineering and healthcare"
    ],
    careerProspects: {
      roles: [
        "Prosthetist",
        "Orthotist",
        "Rehabilitation Specialist",
        "Assistive Technology Expert",
        "Prosthetic Design Engineer",
        "Orthotic Technician",
        "Rehabilitation Engineer",
        "Assistive Device Consultant",
        "Research & Development Specialist",
        "Clinical Coordinator"
      ],
      industries: [
        "Rehabilitation Centers",
        "Prosthetic & Orthotic Companies",
        "Hospitals and Clinics",
        "Assistive Technology Companies",
        "Research Institutions",
        "Government Health Services",
        "NGOs for Disabled",
        "Sports Medicine Centers",
        "Manufacturing Companies",
        "International Aid Organizations"
      ],
      averageSalary: "₹3-8 LPA (varies by experience and specialization)",
      topRecruiters: [
        "Ottobock India",
        "Endolite India",
        "ALIMCO (Artificial Limbs Manufacturing Corporation)",
        "Mobility India",
        "AIIMS Prosthetics Department",
        "CMC Vellore",
        "Manipal Hospitals",
        "Government Rehabilitation Centers",
        "International NGOs",
        "Private P&O Clinics"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Human Anatomy",
          "Biomechanics",
          "Materials Science",
          "Engineering Drawing",
          "Mathematics",
          "Physics",
          "Medical Terminology",
          "Introduction to Prosthetics & Orthotics"
        ]
      },
      {
        year: 2,
        subjects: [
          "Lower Limb Prosthetics",
          "Upper Limb Prosthetics",
          "Spinal Orthotics",
          "Orthotic Design",
          "Gait Analysis",
          "Pathology",
          "Rehabilitation Medicine",
          "Patient Psychology"
        ]
      },
      {
        year: 3,
        subjects: [
          "Advanced Prosthetics",
          "Pediatric P&O",
          "Sports Orthotics",
          "Myoelectric Prosthetics",
          "3D Design and Manufacturing",
          "Clinical Practice",
          "Quality Assurance",
          "Business Management"
        ]
      },
      {
        year: 4,
        subjects: [
          "Advanced Clinical Practice",
          "Research Project",
          "Emerging Technologies",
          "Regulatory Affairs",
          "Professional Ethics",
          "Internship",
          "Comprehensive Examination",
          "Career Development"
        ]
      }
    ],
    admissionProcess: [
      "Apply to colleges offering B.Sc P&O",
      "Entrance examination or merit-based selection",
      "Practical skills assessment",
      "Document verification",
      "Medical fitness certificate",
      "Fee payment and admission",
      "Orientation program",
      "Begin academic and workshop training"
    ],
    topColleges: [
      {
        name: "Christian Medical College",
        location: "Vellore, Tamil Nadu",
        fees: "₹3 LPA",
        ranking: "Premier Medical College"
      },
      {
        name: "All India Institute of Medical Sciences",
        location: "New Delhi",
        fees: "₹20,000 PA",
        ranking: "Top Government Institute"
      },
      {
        name: "Kasturba Medical College",
        location: "Manipal, Karnataka",
        fees: "₹4.5 LPA",
        ranking: "Deemed University"
      },
      {
        name: "JSS College of Physiotherapy",
        location: "Mysore, Karnataka",
        fees: "₹2.5 LPA",
        ranking: "Specialized College"
      },
      {
        name: "Government Institute of Prosthetics",
        location: "Chennai, Tamil Nadu",
        fees: "₹25,000 PA",
        ranking: "Government Institute"
      }
    ],
    skills: [
      "Design and Fabrication",
      "Manual Dexterity",
      "Patient Assessment",
      "Biomechanical Analysis",
      "3D Modeling",
      "Workshop Skills",
      "Problem Solving",
      "Empathy",
      "Communication Skills",
      "Technical Drawing",
      "Quality Control",
      "Innovation"
    ],
    futureScope: "The field is rapidly advancing with 3D printing, smart prosthetics, brain-controlled devices, and advanced materials. Future opportunities include specialization in myoelectric prosthetics, robotic limbs, sports prosthetics, and pediatric devices. The integration of AI, sensors, and IoT technology is creating new possibilities for intelligent assistive devices and remote monitoring systems.",
    faqs: [
      {
        question: "What is the difference between prosthetics and orthotics?",
        answer: "Prosthetics involves creating artificial limbs to replace missing body parts, while orthotics involves designing supportive devices to aid existing but impaired limbs or body parts."
      },
      {
        question: "Can I specialize in either prosthetics or orthotics?",
        answer: "Yes, while the program covers both areas, you can specialize in either prosthetics or orthotics, or focus on specific areas like pediatric devices, sports applications, or advanced technology."
      },
      {
        question: "What are the job prospects in rural areas?",
        answer: "There's significant demand in rural areas through government programs and NGO initiatives. Many graduates work with organizations providing assistive devices to underserved populations."
      },
      {
        question: "Is this field emotionally rewarding?",
        answer: "Yes, helping people regain mobility and independence is highly rewarding. The work directly impacts quality of life and provides visible, meaningful outcomes for patients."
      },
      {
        question: "What technological skills are important?",
        answer: "Modern P&O practice increasingly involves CAD/CAM systems, 3D scanning and printing, biomechanical analysis software, and understanding of electronic and robotic components."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {prostheticsData.title} ({prostheticsData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={prostheticsData} />
      </div>
    </div>
  );
};

export default ProstheticsOrthoticsCoursePage;